import type { LoginResponse } from '@auth/client';
import { LoginForm, AuthProvider as UIAuthProvider } from '@auth/ui-react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { authClient, passkeyClient } from '@/lib/auth-client';
import { useAuth } from '../hooks/use-auth';

/**
 * ログインページコンポーネント。
 *
 * - 認証済みユーザーはダッシュボードへ自動リダイレクト
 * - @auth/ui-react の LoginForm を利用してログインフォームを表示
 * - ログイン成功後にユーザー状態を更新してダッシュボードへ遷移
 *
 * Requirements: 1.1, 1.2, 1.3
 */
function LoginPage() {
  const { isAuthenticated, isLoading, updateUser } = useAuth();
  const navigate = useNavigate();

  // 認証済みユーザーのダッシュボード自動リダイレクト
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void navigate({ to: '/dashboard', replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // 認証成功時: ユーザー状態を更新してダッシュボードへ遷移
  const handleSuccess = useCallback(
    (loginResponse: LoginResponse): void => {
      updateUser(loginResponse.user);
      void navigate({ to: '/dashboard', replace: true });
    },
    [updateUser, navigate],
  );

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <UIAuthProvider client={authClient} passkeyClient={passkeyClient}>
        <LoginForm onSuccess={handleSuccess} />
      </UIAuthProvider>
    </div>
  );
}

export { LoginPage };
